import { FC, ReactNode, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";

import { AccountCircle, ArrowUpward, CheckCircle, Group, Tag } from "@livechat/design-system-icons";
import { DesignToken, IPickerListItem, Picker, SpacingToken, Text } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { AgentPickerListItem, AgentPickerSelectedListItem } from "components/picker-items/agentPickerItems";
import { TeamPickerListItem, TeamPickerSelectedListItem } from "components/picker-items/teamPickerItems";
import PriorityIndicator from "components/priority/PriorityIndicator";
import Role from "constants/role";
import { TicketsRoutes } from "constants/tickets/TicketsRoutes";
import { TICKET_PRIORITY_LABEL_MAP } from "constants/tickets/priorities";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { mapTagsToOptionsWithGrouping } from "helpers/tags";
import { getRouteByPathname } from "helpers/tickets";
import {
  TicketActionTypes,
  TicketPriority,
  TicketSilo,
  TicketStatus,
  getSiloByRoute,
} from "interfaces/entities/ticket";
import { IAgent, selectLoggedInAgent } from "store/entities/agents";
import { ITag } from "store/entities/tags";
import { ITeam } from "store/entities/teams";
import { TicketsViewActions } from "store/views/tickets/actions";
import { textEllipsisStyles } from "styles/common";
import { withVar } from "themes/helpers";

import { ActionWrapper, DropdownWrapper } from "./Dropdowns";
import { IPendingActions } from "./interfaces";

const UNASSIGNED_OPTION_KEY = "unassigned";

const PickerLabel = ({
  label,
  labelSuffix,
  labelBold,
  sublabel,
  leftNode,
}: {
  label: string;
  labelSuffix?: string;
  labelBold?: boolean;
  sublabel?: string;
  leftNode?: ReactNode;
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
}) => (
  <Flex alignItems="center" gap={withVar(SpacingToken.Spacing2)}>
    {leftNode}
    <Flex column justifyContent="center">
      <Text css={textEllipsisStyles} bold={labelBold}>
        {label}{" "}
        <span css={[{ color: withVar(DesignToken.ContentBasicSecondary) }, textEllipsisStyles]}>{labelSuffix}</span>
      </Text>
      {sublabel && (
        <Text size="xs" css={{ color: withVar(DesignToken.ContentBasicSecondary) }}>
          {sublabel}
        </Text>
      )}
    </Flex>
  </Flex>
);

interface IProps {
  actionType: TicketActionTypes;
  pendingActions: IPendingActions;
  setPendingActions: (pendingActions: IPendingActions) => void;
  teams?: ITeam[];
  agents?: IAgent[];
  tags?: ITag[];
  selectedIds?: string[];
  showDeleteModal?: (value: boolean) => void;
  onSpamModalRequest?: (modalType: "spam" | "unspam", selectedIds: string[]) => void;
}

const BulkAction: FC<IProps> = ({
  actionType,
  pendingActions,
  setPendingActions,
  teams,
  agents,
  tags,
  selectedIds,
  showDeleteModal,
  onSpamModalRequest,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const loggedInAgent = useSelector(selectLoggedInAgent);
  const [isStatusWrapperVisible, setStatusWrapperVisible] = useState(false);
  const [isPriorityWrapperVisible, setPriorityWrapperVisible] = useState(false);
  const [isTeamWrapperVisible, setTeamWrapperVisible] = useState(false);
  const [isAgentWrapperVisible, setAgentWrapperVisible] = useState(false);
  const [isTagWrapperVisible, setTagWrapperVisible] = useState(false);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleStatusChange = (options: IPickerListItem[] | null) => {
    setPendingActions({ ...pendingActions, status: options });
    setStatusWrapperVisible(false);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handlePriorityChange = (options: IPickerListItem[] | null) => {
    setPendingActions({ ...pendingActions, priority: options });
    setPriorityWrapperVisible(false);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleTeamIdChange = (options: IPickerListItem[] | null) => {
    setPendingActions({ ...pendingActions, teamId: options });
    setTeamWrapperVisible(false);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleAgentIdChange = (options: IPickerListItem[] | null) => {
    const agentOptions =
      options?.map(option => (option.key === UNASSIGNED_OPTION_KEY ? { ...option, key: "" } : option)) ?? null;

    setPendingActions({ ...pendingActions, agentId: agentOptions });
    setAgentWrapperVisible(false);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleTagChange = (options: IPickerListItem[] | null) => {
    setPendingActions({ ...pendingActions, tagIds: options || [] });
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleSiloChange = (silo: TicketSilo) => {
    dispatch(TicketsViewActions.performBulkSiloChange(selectedIds || [], silo));
    const route = getRouteByPathname(location.pathname) || TicketsRoutes.All;
    const fromSilo = getSiloByRoute(route);
    trackEngagement("Tickets: Change silo actions", EventPlace.BulkActions, {
      from: fromSilo,
      to: silo,
      place: EventPlace.BulkActions,
      bulkChecked: selectedIds?.length,
    });
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleSpamModal = (action: TicketActionTypes) => {
    if (action === TicketActionTypes.MoveToSpam) {
      trackEngagement("Ticket: Blocked list modal opened on bulk spam status change", EventPlace.Tickets);
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      onSpamModalRequest && onSpamModalRequest("spam", selectedIds || []);
    }
    if (action === TicketActionTypes.NotSpam) {
      trackEngagement("Ticket: Trusted list modal opened on bulk spam status change", EventPlace.Tickets);
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      onSpamModalRequest && onSpamModalRequest("unspam", selectedIds || []);
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleSpamSilo = (silo: TicketSilo, action: TicketActionTypes) => {
    handleSiloChange(silo);
    handleSpamModal(action);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleDelete = () => {
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    showDeleteModal && showDeleteModal(true);
  };

  switch (actionType) {
    case TicketActionTypes.SetStatus:
      return (
        <DropdownWrapper
          actionTitle="Set status"
          active={pendingActions.status !== null}
          dropdownTitle="Status"
          icon={CheckCircle}
          isVisible={isStatusWrapperVisible}
          onClick={() => setStatusWrapperVisible(true)}
          onClose={() => setStatusWrapperVisible(false)}
        >
          <Picker
            openedOnInit
            searchDisabled
            placeholder="Choose status…"
            onSelect={handleStatusChange}
            selected={pendingActions.status}
            options={[
              { key: TicketStatus.Open, name: "Open" },
              { key: TicketStatus.Pending, name: "Pending" },
              { key: TicketStatus.OnHold, name: "On hold" },
              { key: TicketStatus.Solved, name: "Solved" },
              { key: TicketStatus.Closed, name: "Closed" },
            ]}
          />
        </DropdownWrapper>
      );
    case TicketActionTypes.SetPriority:
      return (
        <DropdownWrapper
          actionTitle="Set priority"
          active={pendingActions.priority !== null}
          dropdownTitle="Priority"
          icon={ArrowUpward}
          isVisible={isPriorityWrapperVisible}
          onClick={() => setPriorityWrapperVisible(true)}
          onClose={() => setPriorityWrapperVisible(false)}
        >
          <Picker
            openedOnInit
            searchDisabled
            placeholder="Choose priority…"
            onSelect={handlePriorityChange}
            selected={pendingActions.priority}
            options={Object.keys(TicketPriority)
              .filter(key => !Number.isNaN(Number(key)))
              .sort((a, b) => Number(a) - Number(b))
              .map(key => ({
                key,
                name: TICKET_PRIORITY_LABEL_MAP[Number(key) as TicketPriority],
                customElement: {
                  listItemBody: (
                    <PickerLabel
                      leftNode={<PriorityIndicator priority={Number(key) as TicketPriority} />}
                      label={TICKET_PRIORITY_LABEL_MAP[Number(key) as TicketPriority]}
                    />
                  ),
                  selectedItemBody: (
                    <PickerLabel
                      leftNode={<PriorityIndicator priority={Number(key) as TicketPriority} />}
                      label={TICKET_PRIORITY_LABEL_MAP[Number(key) as TicketPriority]}
                    />
                  ),
                },
              }))}
          />
        </DropdownWrapper>
      );
    case TicketActionTypes.AssignTeam:
      return (
        <DropdownWrapper
          actionTitle="Assign team"
          active={pendingActions.teamId !== null}
          dropdownTitle="Team"
          icon={Group}
          isVisible={isTeamWrapperVisible}
          onClick={() => setTeamWrapperVisible(true)}
          onClose={() => setTeamWrapperVisible(false)}
        >
          <Picker
            openedOnInit
            placeholder="Search team…"
            onSelect={handleTeamIdChange}
            selected={pendingActions.teamId}
            options={
              teams?.map(team => ({
                key: team.id,
                name: team.name,
                customElement: {
                  listItemBody: <TeamPickerListItem name={team.name} isDefault={team.isDefault} pickerType="single" />,
                  selectedItemBody: <TeamPickerSelectedListItem name={team.name} pickerType="single" />,
                },
              })) ?? []
            }
          />
        </DropdownWrapper>
      );
    case TicketActionTypes.AssignAgent:
      return (
        <DropdownWrapper
          actionTitle="Assign agent"
          active={pendingActions.agentId !== null}
          dropdownTitle="Agent"
          icon={AccountCircle}
          isVisible={isAgentWrapperVisible}
          onClick={() => setAgentWrapperVisible(true)}
          onClose={() => setAgentWrapperVisible(false)}
        >
          <Picker
            openedOnInit
            placeholder="Search agent…"
            onSelect={handleAgentIdChange}
            selected={pendingActions.agentId}
            options={[
              {
                key: UNASSIGNED_OPTION_KEY,
                name: "Unassigned",
                customElement: {
                  listItemBody: <AgentPickerListItem agentName="Unassigned" secondaryText="" avatarUrl="" />,
                  selectedItemBody: <AgentPickerSelectedListItem agentName="Unassigned" avatarUrl="" />,
                },
              },
              ...(agents
                ?.filter(a => a.permission !== Role.Viewer && a.status === "active")
                .sort(a => (a.id === loggedInAgent.id ? -1 : 1))
                .map(agent => ({
                  key: agent.id,
                  name: agent.name + agent.email,
                  customElement: {
                    listItemBody: (
                      <AgentPickerListItem
                        agentName={agent.name}
                        avatarUrl={agent.avatar}
                        secondaryText={agent.email}
                        isLoggedInAgent={agent.id === loggedInAgent.id}
                      />
                    ),
                    selectedItemBody: <AgentPickerSelectedListItem agentName={agent.name} avatarUrl={agent.avatar} />,
                  },
                })) ?? []),
            ]}
          />
        </DropdownWrapper>
      );
    case TicketActionTypes.AddTag:
      return (
        <DropdownWrapper
          actionTitle="Add tag"
          active={pendingActions.tagIds?.length ?? 0 > 0}
          dropdownTitle="Tag"
          icon={Tag}
          isVisible={isTagWrapperVisible}
          onClick={() => setTagWrapperVisible(true)}
          onClose={() => setTagWrapperVisible(false)}
        >
          <Picker
            type="multi"
            openedOnInit
            placeholder="Search tags…"
            onSelect={handleTagChange}
            selected={pendingActions.tagIds}
            options={mapTagsToOptionsWithGrouping(tags || [], teams || [])}
          />
        </DropdownWrapper>
      );
    case TicketActionTypes.MoveToArchive:
      return <ActionWrapper title="Move to Archive" onClick={() => handleSiloChange(TicketSilo.Archive)} />;
    case TicketActionTypes.MoveToSpam:
      return <ActionWrapper title="Move to Spam" onClick={() => handleSpamSilo(TicketSilo.Spam, actionType)} />;
    case TicketActionTypes.MoveToTrash:
      return (
        <ActionWrapper
          title={`Move to Trash (${selectedIds?.length})`}
          color="warningColor"
          onClick={() => handleSiloChange(TicketSilo.Trash)}
        />
      );
    case TicketActionTypes.DeleteForever:
      return (
        <ActionWrapper title={`Delete forever (${selectedIds?.length})`} color="warningColor" onClick={handleDelete} />
      );
    case TicketActionTypes.NotSpam:
      return <ActionWrapper title="Not spam" onClick={() => handleSpamSilo(TicketSilo.Tickets, actionType)} />;
    case TicketActionTypes.Restore:
      return <ActionWrapper title="Restore" onClick={() => handleSiloChange(TicketSilo.Tickets)} />;
    case TicketActionTypes.Unarchive:
      return <ActionWrapper title="Unarchive" onClick={() => handleSiloChange(TicketSilo.Tickets)} />;
    default:
      return null;
  }
};

export default BulkAction;
